/* eslint-disable */
import React, { useState, useCallback } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    Box,
    Button,
    Typography,
    IconButton,
    Stack,
    CircularProgress,
    Paper,
    Alert,
    Chip,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    useMediaQuery,
    Fade,
    Divider,
    Grid,
    CardActions,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Collapse,
    useTheme,
    alpha
} from '@mui/material';
import {
    Save as SaveIcon,
    NavigateBefore as NavigateBeforeIcon,
    Delete as DeleteIcon,
    Description as DescriptionIcon,
    DirectionsCar as CarIcon,
    Receipt as ReceiptIcon,
    Article as ArticleIcon,
    AttachFile as AttachFileIcon,
    Info as InfoIcon,
    CloudUpload as CloudUploadIcon,
    Close as CloseIcon,
    Visibility as VisibilityIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    PhoneAndroid as MobileIcon
} from '@mui/icons-material';

import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useTravelRequest } from 'hooks/api/travel-request/useTravelRequest';
import secureLocalStorage from 'react-secure-storage';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_FILE_FORMATS = {
    docs: ['.pdf'],
    images: ['.jpg', '.jpeg', '.png']
};

const validationSchema = Yup.object().shape({
    files: Yup.object().shape({
        activityReport: Yup.array()
            .min(1, 'At least one Activity Summary Report is required')
            .required('Activity Summary Report is required'),
        vehicleLog: Yup.array().min(1, 'At least one Vehicle Log Sheet is required').required('Vehicle Log Sheet is required'),
        refundSlip: Yup.array(),
        other: Yup.array()
    }),
    descriptions: Yup.object().shape({
        activityReport: Yup.string(),
        vehicleLog: Yup.string(),
        refundSlip: Yup.string(),
        other: Yup.string()
    })
});

const FilePreviewDialog = ({ open, file, onClose, isMobile }) => (
    <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth={isMobile ? "sm" : "md"} 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
            sx: {
                borderRadius: isMobile ? 0 : 2,
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                m: isMobile ? 0 : 2
            }
        }}
    >
        <DialogTitle sx={{ 
            background: '#1e3a8a',
            color: 'white',
            py: isMobile ? 1.5 : 2,
            px: isMobile ? 2 : 3
        }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="600">
                    Document Preview
                </Typography>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0, position: 'relative' }}>
            {file && (
                <Box 
                    component="img" 
                    src={file} 
                    alt="Preview" 
                    sx={{
                        width: '100%',
                        maxHeight: isMobile ? 'calc(100vh - 120px)' : '70vh',
                        objectFit: 'contain',
                        display: 'block'
                    }} 
                />
            )}
        </DialogContent>
        {!isMobile && (
            <DialogActions sx={{ p: 2 }}>
                <Button 
                    onClick={onClose} 
                    variant="contained"
                    sx={{
                        background: '#1e3a8a',
                        '&:hover': { background: '#1e40af' }
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        )}
    </Dialog>
);

const FileItem = ({ file, index, onRemove, onPreview, disabled, isMobile }) => {
    const isImage = file.type.startsWith('image/');
    const fileSize = (file.size / 1024 / 1024).toFixed(2);

    return (
        <ListItem
            sx={{
                bgcolor: 'grey.50',
                borderRadius: 1,
                mb: 1,
                border: '1px solid',
                borderColor: 'grey.200',
                py: isMobile ? 0.5 : 1,
                px: isMobile ? 1 : 2
            }}
        >
            <ListItemIcon sx={{ minWidth: isMobile ? 32 : 40 }}>
                {isImage ? (
                    <Avatar 
                        src={URL.createObjectURL(file)} 
                        variant="rounded" 
                        sx={{ width: isMobile ? 28 : 32, height: isMobile ? 28 : 32 }}
                    />
                ) : (
                    <Avatar sx={{ 
                        width: isMobile ? 28 : 32, 
                        height: isMobile ? 28 : 32, 
                        bgcolor: '#1e3a8a' 
                    }}>
                        <AttachFileIcon sx={{ fontSize: isMobile ? 14 : 16, color: 'white' }} />
                    </Avatar>
                )}
            </ListItemIcon>
            <ListItemText
                primary={
                    <Typography 
                        variant={isMobile ? "caption" : "body2"} 
                        fontWeight="500" 
                        noWrap
                        sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                    >
                        {file.name}
                    </Typography>
                }
                secondary={
                    <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}
                    >
                        {fileSize} MB
                    </Typography>
                }
            />
            <Stack direction="row" spacing={0.5}>
                {isImage && (
                    <IconButton
                        size="small"
                        onClick={() => onPreview(URL.createObjectURL(file))}
                        sx={{ 
                            color: '#1e3a8a',
                            p: isMobile ? 0.5 : 1
                        }}
                    >
                        <VisibilityIcon fontSize={isMobile ? "small" : "small"} />
                    </IconButton>
                )}
                <IconButton
                    size="small"
                    onClick={() => onRemove(index)}
                    disabled={disabled}
                    sx={{ 
                        color: 'error.main',
                        p: isMobile ? 0.5 : 1
                    }}
                >
                    <DeleteIcon fontSize={isMobile ? "small" : "small"} />
                </IconButton>
            </Stack>
        </ListItem>
    );
};

const UploadSection = ({ config, values, setFieldValue, errors, touched, disabled, onFileChange, submitAttempted, setFileError, setStatus }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [previewDialog, setPreviewDialog] = useState({ open: false, file: null });
    const [dragActive, setDragActive] = useState(false);
    const [expanded, setExpanded] = useState(true); // Set to true by default so files show immediately

    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const event = {
                currentTarget: {
                    files: e.dataTransfer.files
                }
            };
            onFileChange(setFieldValue, config.field, config.maxFiles, values.files[config.field] || [])(event);
        }
    }, [config.field, config.maxFiles, values.files, onFileChange, setFieldValue]);

    const hasFiles = values.files[config.field]?.length > 0;
    const hasError = submitAttempted && errors.files?.[config.field];

    return (
        <>
            <Paper
                elevation={1}
                sx={{
                    p: isMobile ? 1.5 : 2,
                    mb: isMobile ? 1.5 : 2,
                    borderRadius: isMobile ? 1.5 : 2,
                    border: '1px solid',
                    borderColor: dragActive ? '#1e3a8a' : hasError ? 'error.main' : 'grey.300',
                    backgroundColor: dragActive ? '#f0f4ff' : 'white',
                    transition: 'all 0.2s ease-in-out'
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
            >
                <Stack spacing={isMobile ? 1.5 : 2}>
                    {/* Header Section */}
                    <Stack 
                        direction={isMobile ? "column" : "row"} 
                        alignItems={isMobile ? "flex-start" : "center"} 
                        spacing={isMobile ? 1 : 2}
                    >
                        <Avatar sx={{ 
                            bgcolor: '#1e3a8a', 
                            width: isMobile ? 36 : 40, 
                            height: isMobile ? 36 : 40 
                        }}>
                            {React.cloneElement(config.icon, { 
                                sx: { fontSize: isMobile ? 18 : 20 } 
                            })}
                        </Avatar>
                        
                        <Box sx={{ flex: 1, width: isMobile ? '100%' : 'auto' }}>
                            <Stack 
                                direction={isMobile ? "column" : "row"} 
                                alignItems={isMobile ? "flex-start" : "center"} 
                                spacing={1}
                            >
                                <Typography 
                                    variant={isMobile ? "subtitle1" : "h6"} 
                                    fontWeight="600" 
                                    color="text.primary"
                                    sx={{ fontSize: isMobile ? '0.9rem' : '1.25rem' }}
                                >
                                    {config.label}
                                </Typography>
                                {config.required && (
                                    <Chip 
                                        size="small" 
                                        label="Required" 
                                        color="error"
                                        sx={{ 
                                            height: isMobile ? 20 : 24,
                                            fontSize: isMobile ? '0.65rem' : '0.75rem'
                                        }}
                                    />
                                )}
                            </Stack>
                            <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ 
                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                    mt: isMobile ? 0.5 : 0
                                }}
                            >
                                {config.description}
                            </Typography>
                        </Box>

                        {/* Upload Button */}
                        <Button
                            variant={hasFiles ? "outlined" : "contained"}
                            component="label"
                            startIcon={<CloudUploadIcon sx={{ fontSize: isMobile ? 16 : 18 }} />}
                            disabled={disabled || values.files[config.field]?.length >= config.maxFiles}
                            size={isMobile ? "small" : "small"}
                            sx={{
                                background: hasFiles ? 'transparent' : '#1e3a8a',
                                borderColor: hasFiles ? '#1e3a8a' : 'transparent',
                                color: hasFiles ? '#1e3a8a' : 'white',
                                minWidth: isMobile ? 100 : 120,
                                height: isMobile ? 36 : 40,
                                fontSize: isMobile ? '0.75rem' : '0.875rem',
                                textTransform: 'none',
                                '&:hover': {
                                    background: hasFiles ? '#f0f4ff' : '#1e40af',
                                    borderColor: hasFiles ? '#1e40af' : 'transparent'
                                }
                            }}
                        >
                            {values.files[config.field]?.length >= config.maxFiles ? 'Full' : 'Upload'}
                            <input
                                type="file"
                                hidden
                                multiple
                                accept={config.acceptedFormats.join(',')}
                                onChange={(e) => {
                                    const selectedFiles = Array.from(e.target.files);
                                    
                                    // Only filter out Word docs and Excel files, allow PDFs and images
                                    const wordDocs = selectedFiles.filter((file) => {
                                        const fileName = file.name.toLowerCase();
                                        return fileName.endsWith('.doc') || fileName.endsWith('.docx') || 
                                               fileName.endsWith('.odt') || fileName.endsWith('.xlsx') || 
                                               fileName.endsWith('.xls');
                                    });

                                    if (wordDocs.length > 0) {
                                        setFileError(
                                            'Only PDF and image files (JPG, PNG) are allowed. Word documents and Excel files are not supported.'
                                        );
                                        return;
                                    }

                                    onFileChange(setFieldValue, config.field, config.maxFiles, values.files[config.field] || [])(e);
                                }}
                            />
                        </Button>
                    </Stack>

                    {/* Files Section - Always show when there are files */}
                    {hasFiles && (
                        <Box>
                            <Stack 
                                direction="row" 
                                justifyContent="space-between" 
                                alignItems="center" 
                                sx={{ mb: 1 }}
                            >
                                <Typography 
                                    variant="subtitle2" 
                                    fontWeight="600" 
                                    sx={{ 
                                        fontSize: isMobile ? '0.8rem' : '0.875rem',
                                        color: 'text.primary' 
                                    }}
                                >
                                    Files ({values.files[config.field].length})
                                </Typography>
                                {!isMobile && (
                                    <IconButton
                                        size="small"
                                        onClick={() => setExpanded(!expanded)}
                                        sx={{ 
                                            color: '#1e3a8a',
                                            p: 0.5
                                        }}
                                    >
                                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                )}
                            </Stack>
                            
                            {/* Show files list - always visible on mobile, toggleable on desktop */}
                            <Collapse in={expanded || isMobile}>
                                <List sx={{ 
                                    p: 0, 
                                    maxHeight: isMobile ? 150 : 200, 
                                    overflow: 'auto',
                                    '&::-webkit-scrollbar': {
                                        width: '4px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: '#f1f1f1',
                                        borderRadius: '2px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: '#1e3a8a',
                                        borderRadius: '2px',
                                    }
                                }}>
                                    {values.files[config.field].map((file, index) => (
                                        <FileItem
                                            key={index}
                                            file={file}
                                            index={index}
                                            onRemove={(idx) => {
                                                const newFiles = values.files[config.field].filter((_, i) => i !== idx);
                                                setFieldValue(`files.${config.field}`, newFiles);
                                            }}
                                            onPreview={(file) => setPreviewDialog({ open: true, file })}
                                            disabled={disabled}
                                            isMobile={isMobile}
                                        />
                                    ))}
                                </List>
                            </Collapse>
                        </Box>
                    )}

                    {/* Description Field */}
                    <Field name={`descriptions.${config.field}`}>
                        {({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                multiline
                                rows={isMobile ? 2 : 2}
                                label="Description"
                                placeholder={config.placeholder}
                                disabled={disabled}
                                size="small"
                                error={submitAttempted && touched.descriptions?.[config.field] && errors.descriptions?.[config.field]}
                                helperText={submitAttempted && touched.descriptions?.[config.field] && errors.descriptions?.[config.field]}
                                sx={{
                                    '& .MuiInputLabel-root': {
                                        fontSize: isMobile ? '0.8rem' : '0.875rem'
                                    },
                                    '& .MuiOutlinedInput-input': {
                                        fontSize: isMobile ? '0.8rem' : '0.875rem'
                                    },
                                    '& .MuiFormHelperText-root': {
                                        fontSize: isMobile ? '0.7rem' : '0.75rem'
                                    }
                                }}
                            />
                        )}
                    </Field>

                    {/* Error Alert */}
                    {hasError && (
                        <Alert 
                            severity="error" 
                            size="small"
                            sx={{ 
                                fontSize: isMobile ? '0.75rem' : '0.875rem',
                                '& .MuiAlert-message': {
                                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                                }
                            }}
                        >
                            {errors.files[config.field]}
                        </Alert>
                    )}

                    {/* Info Alert */}
                    <Alert 
                        severity="info" 
                        size="small"
                        sx={{ 
                            fontSize: isMobile ? '0.7rem' : '0.75rem',
                            '& .MuiAlert-message': {
                                fontSize: isMobile ? '0.7rem' : '0.75rem'
                            }
                        }}
                    >
                        <Typography variant="caption" sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                            {config.acceptedFormats.join(', ')} | Max: {config.maxFiles} | {MAX_FILE_SIZE / 1024 / 1024}MB each
                        </Typography>
                    </Alert>
                </Stack>
            </Paper>
            <FilePreviewDialog
                open={previewDialog.open}
                file={previewDialog.file}
                onClose={() => setPreviewDialog({ open: false, file: null })}
                isMobile={isMobile}
            />
        </>
    );
};

const FileUpload = ({ onBack, requestId }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [fileError, setFileError] = useState('');
    const { acquitTravel } = useTravelRequest();

    const handleFileChange = useCallback(
        (setFieldValue, field, maxFiles, currentFiles) => (event) => {
            const newFiles = Array.from(event.currentTarget.files);
            setFileError('');

            if (currentFiles.length + newFiles.length > maxFiles) {
                setFileError(`Maximum ${maxFiles} files allowed`);
                return;
            }

            // Filter out only Word documents and Excel files, but allow PDFs and images
            const filteredFiles = newFiles.filter((file) => {
                const fileName = file.name.toLowerCase();
                const isWordDocument = fileName.endsWith('.doc') || fileName.endsWith('.docx') || 
                                     fileName.endsWith('.odt');
                const isExcelFile = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
                
                if (isWordDocument || isExcelFile) {
                    setFileError(`${file.name} is not allowed. Only PDF and image files (JPG, PNG) are supported.`);
                    return false;
                }
                return true;
            });

            if (filteredFiles.length === 0) return;

            const invalidFiles = filteredFiles
                .map((file) => {
                    if (file.size > MAX_FILE_SIZE) {
                        return `File ${file.name} is too large (max ${MAX_FILE_SIZE / 1024 / 1024}MB)`;
                    }
                    return null;
                })
                .filter(Boolean);

            if (invalidFiles.length > 0) {
                setFileError(invalidFiles.join('; '));
                return;
            }

            const updatedFiles = [...currentFiles, ...filteredFiles];
            setFieldValue(`files.${field}`, updatedFiles);
        },
        []
    );

    const documentsConfig = [
        {
            field: 'activityReport',
            label: 'Activity Summary Report',
            icon: <ArticleIcon />,
            description: 'Summary of trip activities and outcomes',
            acceptedFormats: [...ACCEPTED_FILE_FORMATS.docs, ...ACCEPTED_FILE_FORMATS.images],
            placeholder: 'Describe activities conducted during the trip...',
            required: true,
            maxFiles: 5,
            documentType: 'REPORT'
        },
        {
            field: 'vehicleLog',
            label: 'Vehicle Log Sheet',
            icon: <CarIcon />,
            description: 'Vehicle usage and mileage documentation',
            acceptedFormats: [...ACCEPTED_FILE_FORMATS.docs, ...ACCEPTED_FILE_FORMATS.images],
            placeholder: 'Describe vehicle usage and routes...',
            required: true,
            maxFiles: 3,
            documentType: 'VEHICLE_LOG_SHEET'
        },
        {
            field: 'refundSlip',
            label: 'Refund Deposit Slip',
            icon: <ReceiptIcon />,
            description: 'Proof of refund deposits',
            acceptedFormats: [...ACCEPTED_FILE_FORMATS.images, ...ACCEPTED_FILE_FORMATS.docs],
            placeholder: 'Provide refund details...',
            required: false,
            maxFiles: 3,
            documentType: 'REFUND_DEPOSIT'
        },
        {
            field: 'other',
            label: 'Other Supporting Documents',
            icon: <DescriptionIcon />,
            description: 'Additional supporting documentation',
            acceptedFormats: [...ACCEPTED_FILE_FORMATS.docs, ...ACCEPTED_FILE_FORMATS.images],
            placeholder: 'Describe additional documents...',
            required: false,
            maxFiles: 5,
            documentType: 'OTHER'
        }
    ];

    const handleSubmit = async (values, { setSubmitting, setStatus }) => {
        setSubmitAttempted(true);
        try {
            const perdiemRequest = JSON.parse(secureLocalStorage.getItem('perdiemReceipt'));
            const expenseData = JSON.parse(secureLocalStorage.getItem('expenseData'));

            const acquittalLines = expenseData
                .map((line) => ({
                    breakfast: parseFloat(line.breakfast) || 0,
                    lunch: parseFloat(line.lunch) || 0,
                    dinner: parseFloat(line.dinner) || 0,
                    incidental: parseFloat(line.incidental) || 0,
                    miscellaneous: parseFloat(line.miscAmt) || 0,
                    accommodation: parseFloat(line.accommodation),
                    date: line.date,
                    accommodationType: parseFloat(line.provedAcc) > 0 ? 'PROVEN' : 'UN_PROVEN',
                    miscDescription: line.miscDesc || ''
                }))
                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

            const filesWithDocTypes = [];
            const documentTypes = [];

            Object.keys(values.files).forEach((docType) => {
                if (values.files[docType].length > 0) {
                    const config = documentsConfig.find((config) => config.field === docType);
                    if (config) {
                        values.files[docType].forEach((file) => {
                            filesWithDocTypes.push({ file, documentType: config.documentType });
                            documentTypes.push(config.documentType);
                        });
                    }
                }
            });

            const allFiles = filesWithDocTypes.map((item) => item.file);

            const payload = {
                acquittal: {
                    acquittalLines,
                    startDate: acquittalLines[0]?.date,
                    endDate: acquittalLines[acquittalLines.length - 1]?.date,
                    documentTypes,
                    clientReceivedAmount: perdiemRequest?.amountReceived ? parseFloat(perdiemRequest.amountReceived) : 0,
                    dateReceived: perdiemRequest?.dateReceived || new Date().toISOString().split('T')[0],
                    remark: values.descriptions.activityReport || ''
                },
                files: allFiles
            };

            await acquitTravel(requestId, payload);
            setStatus({ success: 'Documents uploaded successfully!' });
        } catch (error) {
            console.error('File submission error:', error);
            setStatus({ error: 'Failed to submit files. Please try again.' });
        } finally {
            setSubmitting(false);
        }
    };

    const initialValues = {
        files: {
            activityReport: [],
            vehicleLog: [],
            refundSlip: [],
            other: []
        },
        descriptions: {
            activityReport: '',
            vehicleLog: '',
            refundSlip: '',
            other: ''
        }
    };

    return (
        <Box sx={{ 
            maxWidth: isMobile ? '100%' : 1200, 
            mx: 'auto', 
            p: isMobile ? 1 : 2,
            minHeight: '100vh',
            background: isMobile ? 'white' : 'transparent'
        }}>
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                {({ values, setFieldValue, errors, touched, isSubmitting, status, setStatus }) => (
                    <Form>
                        <Card 
                            elevation={isMobile ? 0 : 2} 
                            sx={{ 
                                borderRadius: isMobile ? 0 : 2,
                                border: isMobile ? 'none' : '1px solid',
                                borderColor: isMobile ? 'transparent' : alpha('#1e3a8a', 0.1)
                            }}
                        >
                            <CardHeader
                                sx={{
                                    background: '#1e3a8a',
                                    color: 'white',
                                    py: isMobile ? 1.5 : 2,
                                    px: isMobile ? 2 : 3
                                }}
                                title={
                                    <Stack direction="row" alignItems="center" spacing={isMobile ? 1.5 : 2}>
                                        <Avatar sx={{ 
                                            bgcolor: 'rgba(255,255,255,0.2)', 
                                            color: 'white',
                                            width: isMobile ? 40 : 48,
                                            height: isMobile ? 40 : 48
                                        }}>
                                            {isMobile ? <MobileIcon /> : <AttachFileIcon />}
                                        </Avatar>
                                        <Box>
                                            <Typography 
                                                variant={isMobile ? "h6" : "h5"} 
                                                fontWeight="600" 
                                                sx={{ 
                                                    color: 'white',
                                                    fontSize: isMobile ? '1.1rem' : '1.5rem'
                                                }}
                                            >
                                                Document Upload
                                            </Typography>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    color: 'white', 
                                                    opacity: 0.9,
                                                    fontSize: isMobile ? '0.8rem' : '0.875rem'
                                                }}
                                            >
                                                Upload required supporting documents
                                            </Typography>
                                        </Box>
                                    </Stack>
                                }
                            />

                            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                                {status?.error && (
                                    <Alert 
                                        severity="error" 
                                        sx={{ 
                                            mb: 2,
                                            fontSize: isMobile ? '0.8rem' : '0.875rem'
                                        }}
                                        onClose={() => setStatus({})}
                                    >
                                        {status.error}
                                    </Alert>
                                )}

                                {status?.success && (
                                    <Alert 
                                        severity="success" 
                                        sx={{ 
                                            mb: 2,
                                            fontSize: isMobile ? '0.8rem' : '0.875rem'
                                        }}
                                    >
                                        {status.success}
                                    </Alert>
                                )}

                                {fileError && (
                                    <Alert 
                                        severity="error" 
                                        sx={{ 
                                            mb: 2,
                                            fontSize: isMobile ? '0.8rem' : '0.875rem'
                                        }}
                                        onClose={() => setFileError('')}
                                    >
                                        {fileError}
                                    </Alert>
                                )}

                                <Grid container spacing={isMobile ? 1 : 2}>
                                    {documentsConfig.map((config) => (
                                        <Grid item xs={12} sm={6} key={config.field}>
                                            <UploadSection
                                                config={config}
                                                values={values}
                                                setFieldValue={setFieldValue}
                                                errors={errors}
                                                touched={touched}
                                                disabled={isSubmitting}
                                                onFileChange={handleFileChange}
                                                submitAttempted={submitAttempted}
                                                setFileError={setFileError}
                                                setStatus={setStatus}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>

                            <CardActions sx={{ 
                                p: isMobile ? 2 : 3, 
                                pt: 0,
                                position: isMobile ? 'sticky' : 'static',
                                bottom: isMobile ? 0 : 'auto',
                                background: isMobile ? 'white' : 'transparent',
                                borderTop: isMobile ? '1px solid' : 'none',
                                borderColor: isMobile ? 'grey.200' : 'transparent',
                                zIndex: isMobile ? 1000 : 'auto'
                            }}>
                                <Stack 
                                    direction={isMobile ? 'column' : 'row'} 
                                    spacing={isMobile ? 1.5 : 2} 
                                    sx={{ width: '100%' }}
                                    justifyContent="space-between"
                                >
                                    <Button 
                                        variant="outlined" 
                                        onClick={onBack} 
                                        startIcon={<NavigateBeforeIcon />} 
                                        disabled={isSubmitting}
                                        fullWidth={isMobile}
                                        sx={{
                                            borderColor: '#1e3a8a',
                                            color: '#1e3a8a',
                                            height: isMobile ? 48 : 40,
                                            fontSize: isMobile ? '0.9rem' : '0.875rem',
                                            textTransform: 'none',
                                            '&:hover': {
                                                borderColor: '#1e40af',
                                                backgroundColor: '#f0f4ff'
                                            }
                                        }}
                                    >
                                        Back
                                    </Button>

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={isSubmitting}
                                        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                        fullWidth={isMobile}
                                        sx={{
                                            background: '#1e3a8a',
                                            height: isMobile ? 48 : 40,
                                            fontSize: isMobile ? '0.9rem' : '0.875rem',
                                            textTransform: 'none',
                                            '&:hover': { background: '#1e40af' }
                                        }}
                                    >
                                        {isSubmitting ? 'Submitting...' : 'Submit Documents'}
                                    </Button>
                                </Stack>
                            </CardActions>
                        </Card>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

export default FileUpload;
